import React, { useRef } from 'react'
import PropTypes from 'prop-types'

import { COLORS } from '_constants/styles'
import { PROPTYPES } from '../helpers/propTypes'
import './styles.scss'

import usePosition from '../../template/hooks/usePosition'
import Text from '../../template/components/Text.jsx'
import { useLazyLoad } from '_hooks'

const CustomizedAxisLabel = (props) => {
  const { x, y, payload, width, rectsRef, index, data } = props
  const barsQtd = data.length
  const size = width / barsQtd

  const { empty } = data[index]

  const [titleRef, titleRect] = usePosition()
  let rectsRefTemp = [...rectsRef.current]
  rectsRefTemp[index] = titleRect
  rectsRef.current = rectsRefTemp

  return (
    <svg x={x - size / 2} y={y} width={size}>
      <Text
        gRef={titleRef}
        value={payload?.value?.toString?.()}
        fontSize={24}
        textAnchor={'middle'}
        fill={!!empty ? '#aaaaaa' : '#3D3D3D'}
        width={size}
        x={0}
        y={0}
        tspanProps={{ textDecoration: !!empty ? 'line-through' : 'none' }}
      />
    </svg>
  )
}

const BarStacked = ({ data, legend, margin, padding, width, height }) => {
  const dataWithEmpty = data.map((item) => {
    const { negative, neutral, positive } = item
    const copy = { ...item }
    if (negative + neutral + positive === 0) {
      copy.empty = 100
    } else {
      copy.empty = 0
    }
    return copy
  })
  const formatter = (value) => (value >= 5 ? `${value}%` : '')
  const formatterEmpty = (value) => (value === 100 ? 'No data available' : '')

  const rectsRef = useRef([])
  const xHeight = Math.max(...rectsRef.current.map((rect) => rect.height), 30)

  const [
    { BarChart, Bar, XAxis, Legend, ResponsiveContainer, LabelList },
    loadingRecharts,
  ] = useLazyLoad(import('recharts'))

  return (
    <If condition={!loadingRecharts}>
      <ResponsiveContainer
        width={width}
        height={height - 150}
        className="stacked-bar-chart-recharts"
      >
        <BarChart
          data={dataWithEmpty}
          margin={margin}
          width={width}
          height={height - 150}
          barSize={width / dataWithEmpty.length - 20}
        >
          <defs>
            <linearGradient id="positiveColor" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS.DS_MEDIUM_GREEN_DARK} />
              <stop offset="95%" stopColor={COLORS.DS_MEDIUM_GREEN_BASE} />
            </linearGradient>
            <linearGradient id="neutralColor" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS.DS_GRAY_92} />
              <stop offset="95%" stopColor={COLORS.DS_GRAY_98} />
            </linearGradient>
            <linearGradient id="negativeColor" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS.DS_RED_DARK} />
              <stop offset="95%" stopColor={COLORS.DS_RED_BASE} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="labels"
            padding={padding}
            height={xHeight}
            tick={<CustomizedAxisLabel data={dataWithEmpty} rectsRef={rectsRef} />}
          />
          <Bar dataKey="negative" stackId="a" fill="url(#negativeColor)" isAnimationActive={false}>
            <LabelList
              dataKey="negative"
              formatter={formatter}
              position="insideMiddle"
              fill="white"
            />
          </Bar>
          <Bar dataKey="neutral" stackId="a" fill="url(#neutralColor)" isAnimationActive={false}>
            <LabelList
              dataKey="neutral"
              formatter={formatter}
              position="insideMiddle"
              fill={COLORS.DS_GRAY_TWO}
            />
          </Bar>
          <Bar dataKey="positive" stackId="a" fill="url(#positiveColor)" isAnimationActive={false}>
            <LabelList
              dataKey="positive"
              formatter={formatter}
              position="insideMiddle"
              fill="white"
            />
          </Bar>
          <Bar dataKey="empty" stackId="a" fill="url(#neutralColor)" isAnimationActive={false}>
            <LabelList
              dataKey="empty"
              formatter={formatterEmpty}
              position="insideMiddle"
              fill={COLORS.DS_GRAY_TWO}
            />
          </Bar>
          <Legend
            layout={legend.layout}
            align={legend.align}
            verticalAlign={legend.verticalAlign}
            iconType={legend.iconType}
            iconSize={legend.iconSize}
            formatter={(name) => name.charAt(0).toUpperCase() + name.slice(1)}
            wrapperStyle={{
              border: '2px solid #EBEBEB',
              borderRadius: '16px',
              margin: '24px auto 0 auto',
              padding: '24px',
              position: 'relative',
              width: 'max-content',
              fontSize: '28px',
              ...legend.wrapperStyle,
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </If>
  )
}

BarStacked.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      labels: PropTypes.string.isRequired,
      positive: PropTypes.number.isRequired,
      neutral: PropTypes.number.isRequired,
      negative: PropTypes.number.isRequired,
    })
  ).isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  legend: PROPTYPES.LEGEND,
  margin: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    left: PropTypes.number,
    bottom: PropTypes.number,
  }),
  padding: PropTypes.shape({
    left: PropTypes.number,
    right: PropTypes.number,
  }),
}

BarStacked.defaultProps = {
  width: 685,
  height: 310,
  legend: {
    layout: 'horizontal',
    align: 'center',
    verticalAlign: 'bottom',
    iconType: 'circle',
    iconSize: 40,
    wrapperStyle: {},
  },
  margin: {
    top: 0,
    right: 0,
    left: 0,
    bottom: 0,
  },
  padding: {
    left: 50,
    right: 50,
  },
}

export default BarStacked
